import React, {useCallback, useEffect, useState} from 'react';

import {fetchSuggestion} from '@web-landing//api/api';

import {log} from '@web-landing/components/SearchPicks/MiniPicks/analytics';
import {ym_log} from '@web-landing/components/SearchPicks/SearchBlock/analytics';

import {ENTER_BUTTON_KEY_CODE} from '@web-landing/constants';

import {AppType} from '@web-landing//types/searchTypes';
import {SearchBarLocation} from '@web-landing/analytics/types/analytics';

import {useSearchAnalytics} from '@web-landing/analytics/hooks/useSearchAnalytics';

import useLocalStorageState from './useLocalStorageState';

export const useSearch = ({
  requestUrl,
  targetLocation,
  autoFocus = true,
}: {
  requestUrl?: string;
  targetLocation: SearchBarLocation;
  autoFocus?: boolean;
}) => {
  const [isInputActive, setIsInputActive] = useState(autoFocus);
  const [inputText, setInputText] = useState('');
  const [suggestionsList, setSuggestionsList] = useState<Array<AppType>>([]);

  const [historyList, setHistoryList] = useLocalStorageState<Array<AppType>>({
    defaultValue: [],
    storageKey: 'history',
  });

  const {
    searchQueryAnalytics,
    searchBarHistoryDeleteAnalytics,
    searchBarHistoryClearAllAnalytics,
    searchBarHistoryShowAnalytics,
    searchBarSuggestionsShowAnalytics,
    searchBarSuggestionsEnterAnalytics,
  } = useSearchAnalytics(targetLocation);

  //Записывает в предложения приложений список, пришедший с ручки
  useEffect(() => {
    if (inputText.length) {
      fetchSuggestion(inputText, requestUrl).then((res) => {
        setSuggestionsList(res?.body?.suggests ?? []);
        searchBarSuggestionsShowAnalytics(inputText, res?.body?.suggests || []);
      });
    }
  }, [inputText, requestUrl, searchBarSuggestionsShowAnalytics]);

  const setHistory = useCallback(
    (item: AppType) => {
      if (!historyList.some((obj) => obj.text === item.text)) {
        setHistoryList([...historyList, item]);
      }
    },
    [setHistoryList, historyList],
  );

  //записывает в объект истории запись из поиска без приложения
  const setEmptyPackageHistoryItem = useCallback(() => {
    if (inputText.trim().length) {
      setHistory({
        text: inputText,
        textMarked: inputText,
        packageName: null,
        iconUrl: null,
      });
    }

    searchQueryAnalytics(inputText);
    log.omicronFromLandingToVitrina();
  }, [inputText, searchQueryAnalytics, setHistory]);

  //удаляет пункт из истории
  const deleteHistoryItem = useCallback(
    (item: AppType) => {
      const updatedList = historyList.filter((elem) => elem.text !== item.text);
      setHistoryList([...updatedList]);

      searchBarHistoryDeleteAnalytics();
    },
    [historyList, setHistoryList, searchBarHistoryDeleteAnalytics],
  );

  //очищает всю историю поиска
  const setClearHistory = useCallback(() => {
    setHistoryList([]);

    searchBarHistoryClearAllAnalytics();
  }, [setHistoryList, searchBarHistoryClearAllAnalytics]);

  //показывает список с предложенными приложениями
  const setShowAppsList = useCallback(
    (event: React.MouseEvent<HTMLElement>) => {
      event.stopPropagation();
      setIsInputActive(true);
    },
    [],
  );

  //прячет выпадающий список приложений
  const setHideAppsList = useCallback(
    (event: React.MouseEvent<HTMLElement>) => {
      event.stopPropagation();
      setIsInputActive(false);
    },
    [setIsInputActive],
  );

  //записывает текст, введенный в инпут в локальный стейт
  const handleInputChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      setInputText(event.target.value);
      setIsInputActive(true);
    },
    [],
  );

  //делает запрос приложения на нажатии enter из поиска
  const handleInputEnterClick = useCallback(
    (event: {keyCode: number}) => {
      if (event.keyCode === ENTER_BUTTON_KEY_CODE && inputText !== '') {
        setEmptyPackageHistoryItem();
        searchBarSuggestionsEnterAnalytics(inputText, suggestionsList);
        ym_log.searchBarSearchButtonClick();

        window.location.href = `https://www.rustore.ru/catalog/search?query=${inputText}`;
      }
    },
    [
      inputText,
      suggestionsList,
      searchBarSuggestionsEnterAnalytics,
      setEmptyPackageHistoryItem,
    ],
  );

  const handleSearchButtonClick = useCallback(() => {
    setEmptyPackageHistoryItem();
    searchBarSuggestionsEnterAnalytics(inputText, suggestionsList);
    ym_log.searchBarSearchButtonClick();
  }, [
    inputText,
    suggestionsList,
    searchBarSuggestionsEnterAnalytics,
    setEmptyPackageHistoryItem,
  ]);

  return {
    inputText,
    historyList,
    isInputActive,
    suggestionsList,
    setHistory,
    deleteHistoryItem,
    setClearHistory,
    setShowAppsList,
    setHideAppsList,
    handleInputChange,
    handleInputEnterClick,
    handleSearchButtonClick,
    setEmptyPackageHistoryItem,
    searchBarHistoryShowAnalytics,
  } as const;
};
